import { render } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { ConferenceChat } from "./chat.js";
import { inspectVideoCapabilities } from "./capabilities.js";
import { formatAudioDetail, formatAudioSummary } from "./audio-diagnostics.js";
import { ConferenceController } from "./conference-controller.js";
import { DEFAULT_MIC_DEVICE_ID, listAudioInputDevices } from "./media-devices.js";
import { PreactRoomUI } from "./preact-room-ui.js";
import { HIGH_VIDEO_MODES, LOW_VIDEO_MODES, isVideoModeSupported } from "./video-codecs.js";

const HIGH_MODE_OPTIONS = [
  ["auto", "Auto · 1280x720 · 30 fps · 1.6 Mbps · lowest CPU codec"],
  ["avc1080", "H.264 / AVC · 1920x1080 · 30 fps · 2.8 Mbps"],
  ["avc720", "H.264 / AVC · 1280x720 · 30 fps · 1.6 Mbps"],
  ["vp8720", "VP8 · 1280x720 · 30 fps · 1.6 Mbps"],
  ["vp9720", "VP9 · 1280x720 · 24 fps · 1.1 Mbps"],
  ["hevc720", "HEVC / H.265 · 1280x720 · 30 fps · 1.0 Mbps"],
  ["av1540", "AV1 · 960x540 · 20 fps · 650 kbps"]
];

const LOW_MODE_OPTIONS = [
  ["auto", "Auto · 480x270 · 12 fps · 200 kbps · hardware preferred"],
  ["avc360", "H.264 / AVC · 640x360 · 15 fps · 300 kbps"],
  ["avc270", "H.264 / AVC · 480x270 · 12 fps · 200 kbps"],
  ["hevc270", "HEVC / H.265 · 480x270 · 12 fps · 150 kbps · hardware preferred"],
  ["vp9270", "VP9 · 480x270 · 12 fps · 170 kbps"],
  ["vp8180", "VP8 · 320x180 · 10 fps · 150 kbps"],
  ["av1180", "AV1 · 320x180 · 8 fps · 80 kbps"]
];

const INITIAL_VIEW = {
  status: { text: "not connected", kind: "" },
  inRoom: false,
  conference: undefined,
  room: undefined,
  localParticipantId: undefined,
  cameraEnabled: false,
  capturing: false,
  preferredVideoSource: "camera",
  activeVideoSource: "none",
  audioEnabled: false,
  microphoneEnabled: false,
  microphoneSpeaking: false,
  audioDiagnostics: {},
  participantState: {},
  participantMedia: {},
  mediaNote: "",
  links: undefined
};

function ConferenceApp() {
  const [view, setView] = useState(INITIAL_VIEW);
  const [messages, setMessages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [capabilityReport, setCapabilityReport] = useState("");
  const [copyLabel, setCopyLabel] = useState("Copy report");
  const [modeSupport, setModeSupport] = useState({ high: {}, low: {} });

  // The adapter is deliberately the only mutable link between Preact and the
  // video pipeline. Everything else in this file is ordinary UI state.
  const uiRef = useRef();
  if (!uiRef.current) uiRef.current = new PreactRoomUI(setView);
  const ui = uiRef.current;

  const chatRef = useRef();
  if (!chatRef.current) {
    chatRef.current = new ConferenceChat({ onMessage: message => setMessages(current => [...current, message]) });
  }

  const controllerRef = useRef();
  if (!controllerRef.current) controllerRef.current = new ConferenceController({ ui, chat: chatRef.current });
  const controller = controllerRef.current;

  useEffect(() => {
    void loadModeSupport(HIGH_VIDEO_MODES, "high", setModeSupport);
    void loadModeSupport(LOW_VIDEO_MODES, "low", setModeSupport);
    return () => controller.leave();
  }, [controller]);

  // После закрытия хостом — чистый старт /demo/conference?closed=1
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("closed") !== "1") return;
    ui.setStatus("conference closed by its owner", false);
    url.searchParams.delete("closed");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
  }, [ui]);

  const query = new URL(window.location.href).searchParams;
  const inviteFromUrl = query.get("invite") || "";
  const adminConferenceId = query.get("conference");
  const ownerSecret = new URLSearchParams(window.location.hash.slice(1)).get("owner");
  const isAdminLink = Boolean(adminConferenceId && ownerSecret);
  const isInviteLink = Boolean(inviteFromUrl);

  async function runEntry(action) {
    setSubmitting(true);
    try {
      await action();
    } catch (error) {
      ui.setStatus(`error: ${friendlyError(error)}`, false);
    } finally {
      setSubmitting(false);
    }
  }

  async function checkCapabilities() {
    setCapabilityReport("Checking browser video capabilities…");
    setCopyLabel("Copy report");
    setCapabilityReport(await inspectVideoCapabilities());
  }

  async function copyCapabilityReport() {
    try {
      await navigator.clipboard.writeText(capabilityReport);
      setCopyLabel("Copied");
    } catch {
      setCopyLabel("Copy failed");
    }
  }

  async function selectVideoModes(change) {
    try {
      await controller.setVideoModes(change);
    } catch (error) {
      ui.setStatus(`camera error: ${friendlyError(error)}`, false);
    }
  }

  return (
    <main class="page">
      <Header status={view.status} />
      {!view.inRoom ? (
        isAdminLink ? (
          <AdminRejoinCard
            submitting={submitting}
            onJoin={({ displayName }) => runEntry(() => controller.joinConference("admin", {
              conferenceId: adminConferenceId,
              ownerSecret,
              displayName
            }))}
          />
        ) : isInviteLink ? (
          <InviteJoinCard
            inviteId={inviteFromUrl}
            submitting={submitting}
            onJoin={({ displayName }) => runEntry(() => controller.joinConference("invite", {
              inviteId: inviteFromUrl,
              displayName
            }))}
          />
        ) : (
          <CreateConferenceCard
            submitting={submitting}
            onCreate={({ title, chatPolicy, displayName }) => runEntry(() => controller.createConference({ title, chatPolicy, displayName }))}
          />
        )
      ) : (
        <ConferenceRoom
          view={view}
          messages={messages}
          ui={ui}
          controller={controller}
          modeSupport={modeSupport}
          capabilityReport={capabilityReport}
          copyLabel={copyLabel}
          onVideoModesChange={selectVideoModes}
          onToggleCamera={() => controller.toggleVideo()}
          onSetVideoSource={source => controller.setPreferredVideoSource(source)}
          onToggleMicrophone={() => controller.toggleMicrophone()}
          onCheckCapabilities={checkCapabilities}
          onCopyReport={copyCapabilityReport}
          onSendChat={text => controller.sendChat(text)}
          onCloseConference={() => runEntry(() => controller.closeConference())}
        />
      )}
    </main>
  );
}

function Header({ status }) {
  return <header class="top-bar">
    <a href="/">← all demos</a>
    <div>
      <h1>FastPubSub Video Meeting</h1>
      <p>Everyone publishes camera and microphone to FastPubSub media subjects — mesh pub/sub, not WebRTC.</p>
    </div>
    <span class={`status ${status.kind}`}>{status.text}</span>
  </header>;
}

function CreateConferenceCard({ submitting, onCreate }) {
  const [title, setTitle] = useState("Team meeting");
  const [chatPolicy, setChatPolicy] = useState("shared");
  const [hostName, setHostName] = useState("Host");

  return <section class="join-card">
    <h2>Create a meeting</h2>
    <p class="muted">The server generates a random FastPubSub tenant, participant identity and ACL-limited token. A meeting title is never a tenant name.</p>
    <form onSubmit={event => { event.preventDefault(); onCreate({ title, chatPolicy, displayName: hostName }); }}>
      <label>Title <input value={title} maxlength="80" required onInput={event => setTitle(event.currentTarget.value)} /></label>
      <label>Your name <input value={hostName} maxlength="48" required onInput={event => setHostName(event.currentTarget.value)} /></label>
      <label>Chat
        <select value={chatPolicy} onChange={event => setChatPolicy(event.currentTarget.value)}>
          <option value="shared">Shared chat</option>
          <option value="disabled">Disabled</option>
        </select>
      </label>
      <button type="submit" disabled={submitting}>Create and join</button>
    </form>
  </section>;
}

function InviteJoinCard({ inviteId, submitting, onJoin }) {
  const [guestName, setGuestName] = useState("Guest");

  return <section class="join-card">
    <h2>Join meeting</h2>
    <p class="muted">You were invited to a FastPubSub meeting. Enter your display name to join.</p>
    <form onSubmit={event => { event.preventDefault(); onJoin({ displayName: guestName }); }}>
      <label>Invite <input value={inviteId} readOnly /></label>
      <label>Your name <input value={guestName} maxlength="48" required onInput={event => setGuestName(event.currentTarget.value)} /></label>
      <button type="submit" disabled={submitting}>Join meeting</button>
    </form>
  </section>;
}

function AdminRejoinCard({ submitting, onJoin }) {
  const [hostName, setHostName] = useState("Host");

  return <section class="join-card">
    <h2>Rejoin as meeting owner</h2>
    <p class="muted">This admin link restores owner capabilities for an existing meeting.</p>
    <form onSubmit={event => { event.preventDefault(); onJoin({ displayName: hostName }); }}>
      <label>Your name <input value={hostName} maxlength="48" required onInput={event => setHostName(event.currentTarget.value)} /></label>
      <button type="submit" disabled={submitting}>Join as admin</button>
    </form>
  </section>;
}

function ConferenceRoom(props) {
  const { view, controller, ui } = props;
  const participants = Object.values(view.room?.participants || {});
  const onlineParticipants = participants.filter(participant => participant.status === "online");
  const [focusedParticipantId, setFocusedParticipantId] = useState(null);
  const [fullscreenParticipantId, setFullscreenParticipantId] = useState(null);

  useEffect(() => {
    const onFullscreen = () => {
      const element = document.fullscreenElement;
      const id = element instanceof HTMLElement ? element.dataset.participantId : "";
      setFullscreenParticipantId(id || null);
    };
    document.addEventListener("fullscreenchange", onFullscreen);
    return () => document.removeEventListener("fullscreenchange", onFullscreen);
  }, []);

  useEffect(() => {
    controller.setVideoViewDemand(fullscreenParticipantId || focusedParticipantId || "");
  }, [controller, fullscreenParticipantId, focusedParticipantId]);

  useEffect(() => {
    if (!focusedParticipantId) {
      return undefined;
    }
    const onKeyDown = event => {
      if (event.key === "Escape" && !document.fullscreenElement) {
        setFocusedParticipantId(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [focusedParticipantId]);

  useEffect(() => {
    if (!focusedParticipantId) {
      return;
    }
    const stillVisible = onlineParticipants.some(
      participant => participant.participant_id === focusedParticipantId
    );
    if (!stillVisible) {
      setFocusedParticipantId(null);
    }
  }, [focusedParticipantId, onlineParticipants]);

  const visibleParticipants = focusedParticipantId
    ? onlineParticipants.filter(participant => participant.participant_id === focusedParticipantId)
    : onlineParticipants;
  const focusedParticipant = focusedParticipantId
    ? onlineParticipants.find(participant => participant.participant_id === focusedParticipantId)
    : undefined;

  return <section class="conference">
    <div class="room-bar">
      <span>{view.conference.title} · meeting · chat: {view.room?.chat_policy ?? view.conference.chat_policy}</span>
      <span class="muted">authority version {view.room?.version ?? "…"}</span>
      {controller.ownerSecret && !view.room?.closed && <button type="button" onClick={props.onCloseConference}>Close meeting</button>}
    </div>
    {view.links && <ShareLinks links={view.links} />}
    {focusedParticipantId && <div class="focus-bar">
      <span>Only {focusedParticipant?.display_name ?? focusedParticipantId}</span>
      <button type="button" onClick={() => setFocusedParticipantId(null)}>Show all videos</button>
    </div>}
    <section class={`tiles${focusedParticipantId ? " focus-mode" : ""}`}>
      {visibleParticipants.map(participant => <ParticipantTile
        key={participant.participant_id}
        participant={participant}
        isLocal={participant.participant_id === view.localParticipantId}
        capturing={view.capturing}
        activeVideoSource={view.activeVideoSource}
        microphoneEnabled={view.microphoneEnabled}
        microphoneSpeaking={view.microphoneSpeaking}
        focusMode={Boolean(focusedParticipantId)}
        ui={ui}
        media={view.participantMedia[participant.participant_id]}
        participantState={view.participantState?.[participant.participant_id]}
        onVolume={value => controller.setParticipantVolume(participant.participant_id, value)}
        onMute={muted => controller.setParticipantMuted(participant.participant_id, muted)}
        onFocus={() => setFocusedParticipantId(participant.participant_id)}
      />)}
    </section>
    <VideoControls {...props} />
    {props.capabilityReport && <CapabilityReport report={props.capabilityReport} copyLabel={props.copyLabel} onCopy={props.onCopyReport} />}
    <section class="bottom-grid">
      <ParticipantStatePanel participants={participants} participantState={view.participantState || {}} localParticipantId={view.localParticipantId} />
      <AuthorityPanel participants={participants} />
      {view.room?.chat_policy === "disabled"
        ? <section class="panel"><h2>Chat</h2><p class="muted">Disabled by the room owner.</p></section>
        : <ChatPanel messages={props.messages} onSend={props.onSendChat} />}
    </section>
  </section>;
}

function ShareLinks({ links }) {
  return <section class="share-card">
    <h3>Meeting created</h3>
    <label>Invite link — safe to share <input value={links.invite} readOnly /></label>
    <label>Admin link — keep private <input value={links.admin} readOnly /></label>
  </section>;
}

function ParticipantTile({ participant, isLocal, capturing, activeVideoSource, microphoneEnabled, microphoneSpeaking, focusMode, ui, media = {}, participantState, onVolume, onMute, onFocus }) {
  const tileRef = useRef(null);
  const videoEnabled = true;
  const remoteState = !isLocal ? participantState : undefined;
  const remoteVideoSource = remoteState?.videoSource === "desktop" ? "desktop" : "camera";
  const localVideoSource = activeVideoSource === "desktop" ? "desktop" : "camera";
  const videoSource = isLocal ? localVideoSource : remoteVideoSource;
  const cameraIsOn = isLocal
    ? capturing
    : Boolean(remoteState && !remoteState.stale && remoteState.camera && videoEnabled);
  const placeholderText = !isLocal && !videoEnabled
    ? "Video disabled"
    : isLocal
      ? (capturing ? "" : (videoSource === "desktop" ? "Desktop off" : "Camera off"))
      : !remoteState || remoteState.stale
        ? "Waiting for signal…"
        : remoteState.camera
          ? (remoteVideoSource === "desktop" ? "Waiting for desktop…" : "Waiting for video…")
          : (remoteVideoSource === "desktop" ? "Desktop off" : "Camera off");
  const micLabel = isLocal
    ? (microphoneEnabled ? (microphoneSpeaking ? " · mic on · speaking" : " · mic on") : " · mic off")
    : remoteState && !remoteState.stale
      ? (remoteState.microphone ? (remoteState.speaking ? " · mic on · speaking" : " · mic on") : " · mic off")
      : "";
  async function toggleFullscreen() {
    const tile = tileRef.current;
    if (!tile) {
      return;
    }
    try {
      if (document.fullscreenElement === tile) {
        await document.exitFullscreen();
      } else {
        await tile.requestFullscreen();
      }
    } catch (error) {
      console.warn("[conference] fullscreen failed", error);
    }
  }

  const isDesktopShare = cameraIsOn && videoSource === "desktop";

  return <article
    data-participant-id={participant.participant_id}
    class={`tile${isLocal ? " me" : ""}${cameraIsOn ? "" : " camera-off"}${focusMode ? " focus-main" : ""}${isDesktopShare ? " tile-desktop" : ""}`}
    ref={element => {
      tileRef.current = element;
      ui.setParticipantTileElement(participant.participant_id, element);
    }}
  >
    {placeholderText ? <div class="placeholder">{placeholderText}</div> : null}
    {isLocal
      ? <video ref={element => ui.setLocalVideoElement(element)} autoplay muted playsInline class="local-video" />
      : <canvas ref={element => ui.setRemoteCanvasElement(participant.participant_id, element)} class="remote-video" />}
    <div class="tile-focus-controls">
      {!focusMode && <button type="button" onClick={onFocus}>Solo</button>}
      <button type="button" onClick={() => { void toggleFullscreen(); }}>Fullscreen</button>
    </div>
    {!isLocal && <div class="tile-media-controls">
      <label>Sound <input type="range" min="0" max="200" value={Math.round((media.volume ?? 1) * 100)} onInput={event => onVolume(Number(event.currentTarget.value) / 100)} /></label>
      <button type="button" onClick={() => onMute(!media.muted)}>{media.muted ? "Unmute" : "Mute"}</button>
    </div>}
    <span class="tile-name">{participant.display_name}{isLocal ? " (you)" : ""}{cameraIsOn && videoSource === "desktop" ? " · desktop" : ""}{micLabel}</span>
  </article>;
}

function VideoControls({ view, controller, ui, modeSupport, onVideoModesChange, onToggleCamera, onSetVideoSource, onToggleMicrophone, onCheckCapabilities }) {
  const [highMode, setHighMode] = useState(controller.highMode);
  const [lowMode, setLowMode] = useState(controller.lowMode);
  const [micDevices, setMicDevices] = useState([]);
  const videoSource = view.preferredVideoSource || "camera";
  const isDesktop = videoSource === "desktop";
  const videoButtonLabel = view.capturing
    ? (isDesktop ? "Stop desktop" : "Stop camera")
    : (isDesktop ? "Share desktop" : "Start camera");

  async function refreshMicDevices() {
    try {
      setMicDevices(await listAudioInputDevices());
    } catch {
      setMicDevices([]);
    }
  }

  useEffect(() => {
    void refreshMicDevices();
    const media = navigator.mediaDevices;
    if (!media?.addEventListener) {
      return undefined;
    }
    const onDeviceChange = () => { void refreshMicDevices(); };
    media.addEventListener("devicechange", onDeviceChange);
    return () => media.removeEventListener("devicechange", onDeviceChange);
  }, []);

  useEffect(() => {
    if (view.microphoneEnabled) {
      void refreshMicDevices();
    }
  }, [view.microphoneEnabled]);

  async function changeMicDevice(event) {
    try {
      await controller.setMicrophoneDevice(event.currentTarget.value);
    } catch (error) {
      ui.setStatus(`microphone error: ${friendlyError(error)}`, false);
    }
  }

  const changeHighMode = event => {
    const nextHighMode = event.currentTarget.value;
    setHighMode(nextHighMode);
    void onVideoModesChange({ highMode: nextHighMode, lowMode });
  };
  const changeLowMode = event => {
    const nextLowMode = event.currentTarget.value;
    setLowMode(nextLowMode);
    void onVideoModesChange({ highMode, lowMode: nextLowMode });
  };
  return <section class="controls">
    <label>Video source
      <select
        value={videoSource}
        disabled={view.capturing || view.room?.closed}
        onChange={event => onSetVideoSource(event.currentTarget.value)}
      >
        <option value="camera">Camera</option>
        <option value="desktop">Desktop</option>
      </select>
    </label>
    <button type="button" disabled={!view.cameraEnabled || view.room?.closed} onClick={onToggleCamera}>{videoButtonLabel}</button>
    <button type="button" disabled={!view.cameraEnabled || view.room?.closed} onClick={() => { void onToggleMicrophone(); }}>{view.microphoneEnabled ? view.microphoneSpeaking ? "Mute microphone · speaking" : "Mute microphone" : "Enable microphone"}</button>
    <label>Microphone
      <select
        value={controller.selectedMicId ?? DEFAULT_MIC_DEVICE_ID}
        disabled={view.room?.closed}
        onChange={event => { void changeMicDevice(event); }}
      >
        <option value={DEFAULT_MIC_DEVICE_ID}>System default</option>
        {micDevices.map(device => <option key={device.deviceId} value={device.deviceId}>{device.label}</option>)}
      </select>
    </label>
    {!isDesktop && <>
    <ModeSelect label="High mode" options={HIGH_MODE_OPTIONS} value={highMode} support={modeSupport.high} onChange={changeHighMode} />
    <ModeSelect label="Low mode" options={LOW_MODE_OPTIONS} value={lowMode} support={modeSupport.low} onChange={changeLowMode} />
    </>}
    <button type="button" onClick={onCheckCapabilities}>Check video capabilities</button>
    <span class="muted">{view.mediaNote}</span>
    <AudioDiagnostics diagnostics={view.audioDiagnostics} participantState={view.participantState} />
  </section>;
}

function AudioDiagnostics({ diagnostics = {}, participantState = {} }) {
  const [open, setOpen] = useState(false);
  const [copyLabel, setCopyLabel] = useState("copy");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    const id = window.setInterval(() => setTick(value => value + 1), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  const presence = remoteAudioPresence(participantState);
  const summary = formatAudioSummary(diagnostics);
  const detail = formatAudioDetail(diagnostics, presence);
  void tick;
  const health = diagnostics.framePackets
    ? Math.round((diagnostics.decodedFrames / diagnostics.framePackets) * 100)
    : 100;
  const healthClass = health >= 98 ? "ok" : health >= 90 ? "warn" : "bad";

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(detail);
      setCopyLabel("copied");
      window.setTimeout(() => setCopyLabel("copy"), 1500);
    } catch {
      setCopyLabel("error");
    }
  };

  return <div class="audio-diagnostics-panel">
    <button type="button" class={`audio-diagnostics-toggle health-${healthClass}`} onClick={() => setOpen(value => !value)} title="Click for detailed audio diagnostics">
      {summary} {open ? "▾" : "▸"}
    </button>
    {open ? <div class="audio-diagnostics-detail">
      <div class="audio-diagnostics-actions">
        <button type="button" onClick={() => { void onCopy(); }}>{copyLabel}</button>
      </div>
      <pre>{detail}</pre>
    </div> : null}
  </div>;
}

function ModeSelect({ label, options, value, support, onChange }) {
  return <label>{label}
    <select value={value} onChange={onChange}>
      {options.map(([mode, text]) => <option key={mode} value={mode} disabled={support[mode] === false}>{support[mode] === false ? `${text} · unavailable` : text}</option>)}
    </select>
  </label>;
}

function CapabilityReport({ report, copyLabel, onCopy }) {
  return <section class="panel capability-card">
    <div class="capability-heading"><h2>Video capability report</h2><button type="button" onClick={onCopy}>{copyLabel}</button></div>
    <pre>{report}</pre>
  </section>;
}

function AuthorityPanel({ participants }) {
  return <section class="panel">
    <h2>Conference authority</h2>
    <p class="muted">Roles and roster come from FastPubSub authority. Online/offline and camera/mic use public.*.state at 1 Hz.</p>
    <ul class="participants">{participants.map(participant => <li key={participant.participant_id}>
      {participant.display_name}<span class="role"> · {participant.role} · {participant.status}</span>
    </li>)}</ul>
  </section>;
}

function ParticipantStatePanel({ participants, participantState, localParticipantId }) {
  const online = participants.filter(participant => participant.status === "online");
  return <section class="panel media-control-panel">
    <h2>Participant state</h2>
    <p class="muted">Heartbeat: public.&lt;role&gt;.&lt;participant_id&gt;.state — role and id from channel ACL; room = tenant.</p>
    <table class="media-control-table">
      <thead>
        <tr><th>Participant</th><th>Camera</th><th>Microphone</th><th>Updated</th></tr>
      </thead>
      <tbody>
        {online.map(participant => {
          const state = participantState[participant.participant_id];
          const isLocal = participant.participant_id === localParticipantId;
          const ageSec = state?.ageMs == null ? "—" : `${Math.round(state.ageMs / 1000)} s`;
          return <tr key={participant.participant_id} class={state?.stale ? "stale" : ""}>
            <td>{participant.display_name}{isLocal ? " (you)" : ""}</td>
            <td>{mediaStateLabel(state, "camera")}</td>
            <td>{mediaStateLabel(state, "microphone", state?.speaking)}</td>
            <td>{state?.stale ? "no signal" : ageSec}</td>
          </tr>;
        })}
      </tbody>
    </table>
  </section>;
}

/** Short on/off/waiting label for the panel. */
function mediaStateLabel(state, field, speaking = false) {
  if (!state) return "waiting…";
  if (state.stale) return "—";
  if (!state[field]) return "off";
  return speaking ? "on · speaking" : "on";
}

/**
 * Есть ли удалённый участник с микрофоном / говорящий — для подсказок звука.
 * @param {Record<string, { local?: boolean, microphone?: boolean, speaking?: boolean, stale?: boolean }>} participantState
 */
function remoteAudioPresence(participantState) {
  let remoteMicOn = false;
  let remoteSpeaking = false;
  for (const state of Object.values(participantState || {})) {
    if (!state || state.local || state.stale) continue;
    if (state.microphone) remoteMicOn = true;
    if (state.speaking) remoteSpeaking = true;
  }
  return { remoteMicOn, remoteSpeaking };
}

function ChatPanel({ messages, onSend }) {
  const [text, setText] = useState("");
  const logRef = useRef();
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [messages]);
  async function submit(event) {
    event.preventDefault();
    const message = text.trim();
    if (!message) return;
    try { await onSend(message); setText(""); } catch (error) { console.error("[conference] chat send failed", error); }
  }
  return <section class="panel chat-panel">
    <h2>Chat</h2>
    <div ref={logRef} class="chat-log">{messages.map(message => <div key={message.id} class="chat-item"><strong>{message.name}: </strong>{message.text}</div>)}</div>
    <form class="chat-form" onSubmit={submit}><input value={text} maxlength="500" placeholder="Message" required onInput={event => setText(event.currentTarget.value)} /><button type="submit">Send</button></form>
  </section>;
}

async function loadModeSupport(modes, quality, setModeSupport) {
  const result = {};
  await Promise.all(Object.entries(modes).map(async ([name, mode]) => { result[name] = await isVideoModeSupported(mode, quality); }));
  setModeSupport(current => ({ ...current, [quality]: result }));
}

function friendlyError(error) { return error?.message || String(error); }

render(<ConferenceApp />, document.getElementById("app-root"));
